import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { folderService } from '../services/folderService';
import type { ProjectFolder, ProjectFolderContextValue } from '../types';

/**
 * Pastas de projetos, num contexto proprio.
 *
 * Fica fora do ProjectContext de proposito: pasta e uma entidade com ciclo de
 * vida independente (existe vazia, sobrevive a exclusao dos projetos dentro
 * dela), e o ProjectContext ja passa de 2900 linhas. O que atravessa os dois e
 * so `projects.folder_id`, e quem grava isso e o ProjectContext, que e dono da
 * lista de projetos.
 *
 * A leitura nao filtra por organizacao: o RLS ja devolve exatamente as pastas
 * que este usuario pode ver (as pessoais dele mais as das orgs de que
 * participa), e a sidebar mostra todas as orgs de uma vez, sem depender da org
 * "corrente".
 */
const ProjectFolderContext = createContext<ProjectFolderContextValue | null>(null);

export function ProjectFolderProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const [folders, setFolders] = useState<ProjectFolder[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshFolders = useCallback(async () => {
    if (!user?.id) {
      setFolders([]);
      setLoading(false);
      return;
    }

    try {
      setFolders(await folderService.list());
    } catch (err) {
      console.error('Erro ao carregar pastas:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    refreshFolders();
  }, [refreshFolders]);

  const createFolder = useCallback(async (
    name: string,
    organizationId: string | null = null
  ): Promise<ProjectFolder | null> => {
    if (!user?.id) return null;

    const folder = await folderService.create(name, user.id, organizationId);
    setFolders(prev => [...prev, folder].sort((a, b) => a.name.localeCompare(b.name)));
    return folder;
  }, [user?.id]);

  const renameFolder = useCallback(async (id: string, name: string) => {
    const updated = await folderService.rename(id, name);
    setFolders(prev =>
      prev.map(f => f.id === id ? updated : f).sort((a, b) => a.name.localeCompare(b.name))
    );
  }, []);

  /**
   * Some com a pasta do estado, e so. Os projetos que estavam nela ganham
   * `folder_id` nulo pelo `ON DELETE SET NULL`, mas quem recarrega a lista de
   * projetos e quem chama: o agrupamento ja trata pasta desconhecida como "sem
   * pasta", entao a tela fica correta mesmo antes do refetch.
   */
  const deleteFolder = useCallback(async (id: string) => {
    await folderService.remove(id);
    setFolders(prev => prev.filter(f => f.id !== id));
  }, []);

  const value: ProjectFolderContextValue = useMemo(() => ({
    folders,
    loading,
    createFolder,
    renameFolder,
    deleteFolder,
    refreshFolders,
  }), [folders, loading, createFolder, renameFolder, deleteFolder, refreshFolders]);

  return (
    <ProjectFolderContext.Provider value={value}>
      {children}
    </ProjectFolderContext.Provider>
  );
}

export function useProjectFolders(): ProjectFolderContextValue {
  const context = useContext(ProjectFolderContext);
  if (!context) {
    throw new Error('useProjectFolders must be used within ProjectFolderProvider');
  }
  return context;
}
